import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import { format, isSameDay, isAfter, startOfDay, subDays } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { bookingAPI } from '../../api/bookings';
import { businessAPI } from '../../api/businesses';
import RecentBookings from '../dashboard/RecentBookings';
import ReviewForm from '../reviews/ReviewForm';
import { resolveImageUrl } from '../../utils/formatters';
import { 
  CalendarIcon, 
  ClockIcon, 
  UserIcon, 
  IdentificationIcon,
  PlusIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import 'react-calendar/dist/Calendar.css';

const BookingsCalendar = () => {
  const navigate = useNavigate();
  const { isBusinessOwner, isStaff } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [allBookings, setAllBookings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customerAvatarErrors, setCustomerAvatarErrors] = useState({});
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedBookingForAction, setSelectedBookingForAction] = useState(null);
  const [newDate, setNewDate] = useState(new Date());
  const [newTime, setNewTime] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotMessage, setSlotMessage] = useState('');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedBookingForReview, setSelectedBookingForReview] = useState(null);

  useEffect(() => {
    loadBookings();
  }, [selectedDate]);

  const getBookingDateStr = (booking) => {
    if (booking?.booking_date_local) return booking.booking_date_local;
    if (!booking?.booking_date) return '';
    return typeof booking.booking_date === 'string'
      ? booking.booking_date.split('T')[0]
      : format(new Date(booking.booking_date), 'yyyy-MM-dd');
  };

  const loadBookings = async () => {
    setLoading(true);
    try {
      const response = await bookingAPI.getMyBookings();
      const bookingsData = response.data.data || [];
      setAllBookings(bookingsData);

      // If selected date has no bookings and there are bookings, auto-select the date of next/most recent booking
      let effectiveSelectedDate = selectedDate;
      if (bookingsData.length > 0) {
        const dateStr = format(effectiveSelectedDate, 'yyyy-MM-dd');
        const hasBookingsOnDate = bookingsData.some(b => getBookingDateStr(b) === dateStr);
        
        if (!hasBookingsOnDate) {
          // Find next upcoming booking date, or most recent past booking date
          const sortedByDate = [...bookingsData].sort((a, b) => 
            new Date(getBookingDateStr(a)).getTime() - new Date(getBookingDateStr(b)).getTime()
          );
          const today = new Date();
          const todayStr = format(today, 'yyyy-MM-dd');
          
          // Find first booking on or after today
          const nextBooking = sortedByDate.find(b => getBookingDateStr(b) >= todayStr);
          // If none, take the last (most recent) booking
          const bookingToUse = nextBooking || sortedByDate[sortedByDate.length - 1];
          if (bookingToUse) {
            effectiveSelectedDate = new Date(getBookingDateStr(bookingToUse) + 'T00:00:00');
            setSelectedDate(effectiveSelectedDate);
          }
        }
      }
      
      const dateStr = format(effectiveSelectedDate, 'yyyy-MM-dd');
      const filtered = bookingsData.filter(b => getBookingDateStr(b) === dateStr);
      
      setBookings(filtered);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSlots = async (bookingId, businessId, serviceId, staffId, date) => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await bookingAPI.getAvailableSlots({
        business_id: businessId,
        service_id: serviceId,
        staff_id: staffId || null,
        date: dateStr
      });
      
      if (response.data && response.data.data && response.data.data.available_slots) {
        const slots = response.data.data.available_slots
          .filter(slot => slot.available != false && slot.available != 0)
          .map(slot => ({
            ...slot,
            display_time: slot.display_time_local || slot.display_time || (slot.start_time && slot.end_time ? `${slot.start_time} - ${slot.end_time}` : (slot.start_time || ''))
          }));
        setAvailableSlots(slots);
        setSlotMessage(slots.length === 0 ? (response.data.data.message || 'No available slots for this date') : '');
      } else {
        setAvailableSlots([]);
        setSlotMessage(response.data?.message || 'No available slots for this date');
      }
    } catch (error) {
      console.error('Failed to load available slots:', error);
      setAvailableSlots([]);
    }
  };

  const handleReschedule = async (booking) => {
    setSelectedBookingForAction(booking);
    const d = booking.booking_date_local
      ? new Date(`${booking.booking_date_local}T00:00:00`)
      : new Date(booking.booking_date);
    setNewDate(d);
    setNewTime(booking.start_time);
    await loadAvailableSlots(booking.id, booking.business_id, booking.service_id, booking.staff_id, d);
    setShowRescheduleModal(true);
  };

  const handleRescheduleSubmit = async () => {
    try {
      await bookingAPI.reschedule(selectedBookingForAction.id, {
        booking_date: format(newDate, 'yyyy-MM-dd'),
        start_time: newTime
      });
      await loadBookings();
      setShowRescheduleModal(false);
      setSelectedBookingForAction(null);
      alert('Booking rescheduled successfully');
    } catch (error) {
      console.error('Failed to reschedule:', error);
      alert('Failed to reschedule booking. Please try again.');
    }
  };

  const handleCancel = (booking) => {
    setSelectedBookingForAction(booking);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleCancelSubmit = async () => {
    if (!cancelReason.trim()) {
      alert('Please provide a reason for cancellation');
      return;
    }
    
    try {
      await bookingAPI.cancel(selectedBookingForAction.id, cancelReason);
      await loadBookings();
      setShowCancelModal(false);
      setSelectedBookingForAction(null);
      alert('Booking cancelled successfully');
    } catch (error) {
      console.error('Failed to cancel:', error);
      alert('Failed to cancel booking. Please try again.');
    }
  };

  const handleUpdateStatus = async (bookingId, newStatus) => {
    try {
      setLoading(true);
      await bookingAPI.updateStatus(bookingId, newStatus);
      await loadBookings();
      alert(`Booking ${newStatus} successfully`);
    } catch (error) {
      console.error(`Failed to update booking status to ${newStatus}:`, error);
      alert(`Failed to ${newStatus} booking. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleStartBooking = async (bookingId) => {
    try {
      setLoading(true);
      await bookingAPI.start(bookingId);
      await loadBookings();
      alert('Booking started successfully');
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to start booking. Please try again.';
      console.error('Failed to start booking:', error);
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteBooking = async (bookingId) => {
    try {
      setLoading(true);
      await bookingAPI.complete(bookingId);
      await loadBookings();
      alert('Booking completed successfully');
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to complete booking. Please try again.';
      console.error('Failed to complete booking:', error);
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'confirmed':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'completed':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const stats = {
    total: allBookings.length,
    upcoming: allBookings.filter(b => isAfter(new Date(b.booking_date), startOfDay(new Date())) || (isSameDay(new Date(b.booking_date), new Date()) && b.status !== 'completed' && b.status !== 'cancelled')).length,
    pending: allBookings.filter(b => b.status === 'pending').length,
    completed: allBookings.filter(b => b.status === 'completed').length,
    cancelled: allBookings.filter(b => b.status === 'cancelled').length
  };

  const getDayBookings = () => {
    const sorted = [...bookings].sort((a, b) => a.start_time.localeCompare(b.start_time));
    
    return {
      pending: sorted.filter(b => b.status === 'pending'),
      upcoming: sorted.filter(b => b.status === 'confirmed' || b.status === 'in_progress'),
      past: sorted.filter(b => b.status === 'completed' || b.status === 'cancelled')
    };
  };

  const dayBookings = getDayBookings();

  return (
    <>
      <div className="max-w-7xl mx-auto space-y-8 pb-12">
      {/* Custom Calendar Styling */}
      <style>{`
        .react-calendar {
          width: 100%;
          border: none;
          font-family: inherit;
          padding: 10px;
        }
        .react-calendar__navigation button {
          color: #4f46e5;
          font-weight: 600;
          font-size: 1.1rem;
        }
        .react-calendar__month-view__weekdays {
          text-transform: uppercase;
          font-weight: bold;
          font-size: 0.75rem;
          color: #6b7280;
        }
        .react-calendar__tile {
          padding: 12px 8px;
          border-radius: 8px;
          transition: all 0.2s;
        }
        .react-calendar__tile--now {
          background: #eef2ff !important;
          color: #4f46e5;
          font-weight: bold;
        }
        .react-calendar__tile--active {
          background: #4f46e5 !important;
          color: white !important;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        }
        .has-booking {
          position: relative;
        }
        .has-booking::after {
          content: '';
          position: absolute;
          bottom: 4px;
          left: 50%;
          transform: translateX(-50%);
          width: 4px;
          height: 4px;
          background-color: #4f46e5;
          border-radius: 50%;
        }
      `}</style>

      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            {isBusinessOwner || isStaff ? 'Business Schedule' : 'Your Bookings'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isBusinessOwner || isStaff 
              ? 'View and manage customer appointments for your business.' 
              : 'Manage your appointments and schedule new ones.'}
          </p>
        </div>
        {!(isBusinessOwner || isStaff) && (
          <button
            onClick={() => navigate('/bookings/create')}
            className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:bg-indigo-700 transition-all transform hover:-translate-y-0.5 active:scale-95"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Create New Booking
          </button>
        )}
      </div>

      {/* Stats Quick View */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Total', value: stats.total, icon: CalendarIcon, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Upcoming', value: stats.upcoming, icon: ClockIcon, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Pending', value: stats.pending, icon: ClockIcon, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Completed', value: stats.completed, icon: CheckCircleIcon, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Cancelled', value: stats.cancelled, icon: XCircleIcon, color: 'text-rose-600', bg: 'bg-rose-50' },
        ].map((stat, idx) => (
          <div key={idx} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
            <div className={`p-3 rounded-xl ${stat.bg} ${stat.color}`}>
              <stat.icon className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">{stat.label}</p>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Calendar */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white rounded-3xl shadow-xl shadow-indigo-100/50 border border-gray-100 overflow-hidden">
            <div className="p-6 bg-indigo-600 text-white">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                Select Date
              </h2>
            </div>
            <div className="p-4">
              <Calendar
                onChange={setSelectedDate}
                value={selectedDate}
                tileClassName={({ date }) => {
                  const dateStr = format(date, 'yyyy-MM-dd');
                  const hasBookings = allBookings.some(b => getBookingDateStr(b) === dateStr);
                  return hasBookings ? 'has-booking' : '';
                }}
              />
            </div>
          </div>

          <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-100">
            <h3 className="font-bold text-indigo-900 mb-2 flex items-center gap-2">
              <FunnelIcon className="w-4 h-4" />
              Quick Filter
            </h3>
            <p className="text-sm text-indigo-700">Click any date with a dot below it to see specific bookings for that day.</p>
          </div>
        </div>
        
        {/* Right Column: Bookings for Selected Date */}
        <div className="lg:col-span-8 space-y-6">
          <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden min-h-[500px]">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                <div className="w-2 h-8 bg-indigo-600 rounded-full"></div>
                Bookings for {format(selectedDate, 'MMMM dd, yyyy')}
              </h2>
              <span className="px-3 py-1 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-600 shadow-sm">
                {bookings.length} {bookings.length === 1 ? 'Appointment' : 'Appointments'}
              </span>
            </div>

            <div className="p-6">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <ArrowPathIcon className="w-10 h-10 text-indigo-600 animate-spin" />
                  <p className="text-gray-500 font-medium">Updating list...</p>
                </div>
              ) : bookings.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-300">
                    <CalendarIcon className="w-10 h-10" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">No appointments found</h3>
                  <p className="text-gray-500 max-w-xs mx-auto mt-2 mb-8">
                    {isBusinessOwner || isStaff 
                      ? "There are no customer bookings scheduled for this date or the previous day." 
                      : "You don't have any bookings scheduled for this date."}
                  </p>
                  {!(isBusinessOwner || isStaff) && (
                    <button
                      onClick={() => navigate('/bookings/create')}
                      className="btn-primary flex items-center gap-2 shadow-lg shadow-indigo-200"
                    >
                      <PlusIcon className="w-5 h-5" />
                      Book Something Now
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Pending Confirmation Section */}
                  {dayBookings.pending.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-bold text-amber-500 uppercase tracking-widest flex items-center gap-2">
                        <ClockIcon className="w-4 h-4" />
                        Pending Confirmation
                      </h3>
                      <div className="grid gap-4">
                        {dayBookings.pending.map((booking) => (
                          <div 
                            key={booking.id} 
                            className="group bg-amber-50/30 border border-amber-100 rounded-2xl p-5 hover:border-amber-200 hover:shadow-lg hover:shadow-amber-50 transition-all relative overflow-hidden"
                          >
                            <div className="flex flex-col md:flex-row justify-between gap-6 relative z-10">
                              <div className="flex gap-4">
                                <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 bg-amber-100 text-amber-600">
                                  <IdentificationIcon className="w-8 h-8 opacity-80" />
                                </div>
                                <div className="space-y-1">
                                  <div className="flex items-center gap-3">
                                    <h3 className="text-lg font-bold text-gray-900">{booking.service_name}</h3>
                                    <span className="px-3 py-0.5 text-xs font-bold uppercase rounded-full border bg-amber-100 text-amber-700 border-amber-200">
                                      Pending
                                    </span>
                                  </div>
                                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                                    <p className="text-sm text-gray-600 flex items-center gap-1.5 font-medium">
                                      <ClockIcon className="w-4 h-4 text-amber-500" />
                                      {booking.end_time_local ? `${booking.start_time_local} - ${booking.end_time_local}` : (booking.start_time_local || booking.start_time)}
                                    </p>
                                    <p className="text-sm text-gray-500 flex items-center gap-1.5">
                                      {booking.customer_profile_image && !customerAvatarErrors[booking.id] ? (
                                        <img
                                          src={resolveImageUrl(booking.customer_profile_image)}
                                          alt={`${booking.customer_first_name || ''} ${booking.customer_last_name || ''}`.trim() || 'Customer'}
                                          className="w-4 h-4 rounded-full object-cover"
                                          onError={() => setCustomerAvatarErrors((prev) => ({ ...prev, [booking.id]: true }))}
                                        />
                                      ) : (
                                        <UserIcon className="w-4 h-4 text-gray-400" />
                                      )}
                                      Customer: {booking.customer_first_name && booking.customer_last_name ? `${booking.customer_first_name} ${booking.customer_last_name}` : (booking.customer_name || 'Anonymous')}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              <div className="flex md:flex-col justify-between items-end gap-3">
                                <div className="text-right">
                                  <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Booking ID</p>
                                  <p className="text-sm font-mono font-medium text-gray-600">#{booking.id}</p>
                                </div>
                                {(isBusinessOwner || isStaff) && (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleUpdateStatus(booking.id, 'confirmed')}
                                      className="px-4 py-2 text-sm font-bold bg-emerald-50 text-emerald-600 rounded-xl hover:bg-emerald-100 transition-colors flex items-center gap-1.5"
                                    >
                                      <CheckCircleIcon className="w-4 h-4" />
                                      Confirm
                                    </button>

                                    <button
                                      onClick={() => handleCancel(booking)}
                                      className="px-4 py-2 text-sm font-bold bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-100 transition-colors flex items-center gap-1.5"
                                    >
                                      <XCircleIcon className="w-4 h-4" />
                                      Cancel
                                    </button>
                                  </div>
                                )}

                                {!(isBusinessOwner || isStaff) && (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleCancel(booking)}
                                      className="px-4 py-2 text-sm font-bold bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-100 transition-colors flex items-center gap-1.5"
                                    >
                                      <XCircleIcon className="w-4 h-4" />
                                      Cancel
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upcoming Appointments Section */}
                  {dayBookings.upcoming.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-bold text-indigo-500 uppercase tracking-widest flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4" />
                        Upcoming Appointments
                      </h3>
                      <div className="grid gap-4">
                        {dayBookings.upcoming.map((booking) => (
                          <div 
                            key={booking.id} 
                            className="group bg-white border border-gray-100 rounded-2xl p-5 hover:border-indigo-200 hover:shadow-lg hover:shadow-indigo-50 transition-all relative overflow-hidden"
                          >
                            <div className="flex flex-col md:flex-row justify-between gap-6 relative z-10">
                              <div className="flex gap-4">
                                <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 bg-indigo-50 text-indigo-600">
                                  <IdentificationIcon className="w-8 h-8 opacity-80" />
                                </div>
                                <div className="space-y-1">
                                  <div className="flex items-center gap-3">
                                    <h3 className="text-lg font-bold text-gray-900">{booking.service_name}</h3>
                                    <span className={`px-3 py-0.5 text-xs font-bold uppercase rounded-full border ${getStatusColor(booking.status)}`}>
                                      {booking.status === 'in_progress' ? 'In Progress' : 'Confirmed'}
                                    </span>
                                  </div>
                                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                                    <p className="text-sm text-gray-600 flex items-center gap-1.5 font-medium">
                                      <ClockIcon className="w-4 h-4 text-indigo-500" />
                                      {booking.end_time_local ? `${booking.start_time_local} - ${booking.end_time_local}` : (booking.start_time_local || booking.start_time)}
                                    </p>
                                    <p className="text-sm text-gray-500 flex items-center gap-1.5">
                                      {booking.customer_profile_image && !customerAvatarErrors[booking.id] ? (
                                        <img
                                          src={resolveImageUrl(booking.customer_profile_image)}
                                          alt={`${booking.customer_first_name || ''} ${booking.customer_last_name || ''}`.trim() || 'Customer'}
                                          className="w-4 h-4 rounded-full object-cover"
                                          onError={() => setCustomerAvatarErrors((prev) => ({ ...prev, [booking.id]: true }))}
                                        />
                                      ) : (
                                        <UserIcon className="w-4 h-4 text-gray-400" />
                                      )}
                                      Customer: {booking.customer_first_name && booking.customer_last_name ? `${booking.customer_first_name} ${booking.customer_last_name}` : (booking.customer_name || 'Anonymous')}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              <div className="flex md:flex-col justify-between items-end gap-3">
                                <div className="text-right">
                                  <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Booking ID</p>
                                  <p className="text-sm font-mono font-medium text-gray-600">#{booking.id}</p>
                                </div>

                                <div className="flex gap-2">
                                  <button
                                    onClick={() => navigate(`/bookings/${booking.id}`)}
                                    className="px-4 py-2 text-sm font-bold bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-colors"
                                  >
                                    Chat
                                  </button>

                                  {(isBusinessOwner || isStaff) && booking.status === 'confirmed' && (
                                    <button
                                      onClick={() => handleStartBooking(booking.id)}
                                      className="px-4 py-2 text-sm font-bold bg-emerald-50 text-emerald-700 rounded-xl hover:bg-emerald-100 transition-colors"
                                    >
                                      Start
                                    </button>
                                  )}

                                  {(isBusinessOwner || isStaff) && booking.status === 'in_progress' && (
                                    <button
                                      onClick={() => handleCompleteBooking(booking.id)}
                                      className="px-4 py-2 text-sm font-bold bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 transition-colors"
                                    >
                                      Complete
                                    </button>
                                  )}

                                  {booking.status === 'confirmed' && (
                                    <button
                                      onClick={() => handleCancel(booking)}
                                      className="px-4 py-2 text-sm font-bold bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-100 transition-colors flex items-center gap-1.5"
                                    >
                                      <XCircleIcon className="w-4 h-4" />
                                      Cancel
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Past Activity Section */}
                  {dayBookings.past.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                        <ClockIcon className="w-4 h-4" />
                        Past Activity
                      </h3>
                      <div className="grid gap-4 opacity-75">
                        {dayBookings.past.map((booking) => (
                          <div 
                            key={booking.id} 
                            className="group bg-gray-50/50 border border-gray-100 rounded-2xl p-5 hover:bg-white hover:border-gray-200 transition-all"
                          >
                            <div className="flex flex-col md:flex-row justify-between gap-6">
                              <div className="flex gap-4">
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${getStatusColor(booking.status).split(' ')[0]} ${getStatusColor(booking.status).split(' ')[1]}`}>
                                  <IdentificationIcon className="w-8 h-8 opacity-60" />
                                </div>
                                <div className="space-y-1">
                                  <div className="flex items-center gap-3">
                                    <h3 className="text-lg font-bold text-gray-600">{booking.service_name}</h3>
                                    {booking.status !== 'completed' && (
                                      <span className={`px-3 py-0.5 text-xs font-bold uppercase rounded-full border ${getStatusColor(booking.status)}`}>
                                        {booking.status}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                                    <p className="text-sm text-gray-500 flex items-center gap-1.5">
                                      <ClockIcon className="w-4 h-4 text-gray-400" />
                                      {booking.end_time_local ? `${booking.start_time_local} - ${booking.end_time_local}` : (booking.start_time_local || booking.start_time)}
                                    </p>
                                    <p className="text-sm text-gray-400 flex items-center gap-1.5">
                                      {booking.customer_profile_image && !customerAvatarErrors[booking.id] ? (
                                        <img
                                          src={resolveImageUrl(booking.customer_profile_image)}
                                          alt={`${booking.customer_first_name || ''} ${booking.customer_last_name || ''}`.trim() || 'Customer'}
                                          className="w-4 h-4 rounded-full object-cover opacity-70"
                                          onError={() => setCustomerAvatarErrors((prev) => ({ ...prev, [booking.id]: true }))}
                                        />
                                      ) : (
                                        <UserIcon className="w-4 h-4 text-gray-300" />
                                      )}
                                      Customer: {booking.customer_first_name && booking.customer_last_name ? `${booking.customer_first_name} ${booking.customer_last_name}` : (booking.customer_name || 'Anonymous')}
                                    </p>
                                    {booking.status === 'cancelled' && booking.cancellation_reason && (
                                      <p className="text-sm text-rose-700 font-medium">
                                        Reason: {booking.cancellation_reason}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex md:flex-col justify-between items-end gap-3">
                                <div className="text-right">
                                  <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Booking ID</p>
                                  <p className="text-sm font-mono font-medium text-gray-600">#{booking.id}</p>
                                </div>
                                <div className="flex gap-2">
                                  {(isBusinessOwner || isStaff) && booking.status === 'pending' && (
                                    <button
                                      onClick={() => handleUpdateStatus(booking.id, 'confirmed')}
                                      className="px-4 py-2 text-sm font-bold bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors flex items-center gap-1.5"
                                    >
                                      <CheckCircleIcon className="w-4 h-4" />
                                      Confirm
                                    </button>
                                  )}
                                  {!isBusinessOwner && !isStaff && booking.status === 'completed' && !booking.has_review && (
                                    <button
                                      onClick={() => {
                                        setSelectedBookingForReview(booking);
                                        setShowReviewForm(true);
                                      }}
                                      className="px-4 py-2 text-sm font-bold bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
                                    >
                                      Write Review
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>

    {/* Reschedule Modal */}
      {showRescheduleModal && selectedBookingForAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Reschedule Booking</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">New Date</label>
                <input
                  type="date"
                  value={format(newDate, 'yyyy-MM-dd')}
                  min={format(new Date(), 'yyyy-MM-dd')}
                  onChange={(e) => {
                    setNewDate(new Date(e.target.value));
                    loadAvailableSlots(
                      selectedBookingForAction.id,
                      selectedBookingForAction.business_id,
                      selectedBookingForAction.service_id,
                      selectedBookingForAction.staff_id,
                      new Date(e.target.value)
                    );
                  }}
                  className="w-full input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">New Time</label>
                <select
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  className="w-full input"
                  required
                >
                  <option value="">Select a time slot</option>
                  {availableSlots.map((slot, index) => (
                    <option key={index} value={slot.start_time}>
                      {slot.display_time}
                    </option>
                  ))}
                </select>
                {availableSlots.length === 0 && newDate && (
                  <p className="text-sm text-amber-600 mt-1">{slotMessage || 'No available slots for this date'}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowRescheduleModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRescheduleSubmit}
                disabled={!newTime}
                className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
              >
                Confirm Reschedule
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && selectedBookingForAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Cancel Booking</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to cancel your appointment for{' '}
              <span className="font-semibold">{selectedBookingForAction.service_name}</span>?
            </p>
            <div>
              <label className="block text-sm font-medium mb-1">Reason for cancellation *</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows="3"
                className="w-full input"
                placeholder="Please tell us why you're cancelling..."
                required
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Go Back
              </button>
              <button
                onClick={handleCancelSubmit}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Confirm Cancellation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Form Modal */}
      {showReviewForm && selectedBookingForReview && (
        <ReviewForm
          bookingId={selectedBookingForReview.id}
          businessName={selectedBookingForReview.business_name}
          onReviewSubmitted={() => {
            setShowReviewForm(false);
            setSelectedBookingForReview(null);
            loadBookings();
          }}
          onCancel={() => {
            setShowReviewForm(false);
            setSelectedBookingForReview(null);
          }}
        />
      )}
    </>
  );
};

export default BookingsCalendar;
